import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './FieldSample.scss';
import { GridContainer, Grid } from '../../shared/grid';
import { useStoreContext } from '../../store/context';
import { Autocomplete, TextField, Button, IconButton } from '@mui/material';
import { errorResponse, checkAdminRole, fieldSampleAreaEditor, fieldSampleGroupEditor, checkUserFieldSampleCollector, validateCharacters, ValidCharacters } from '../../store/common';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import {  useHistory, useLocation } from 'react-router-dom';
import CustomTablePagination from '../../shared/TablePagination/CustomTablePagination';
import { Group } from './Groups/Group';
import { Contact } from 'src/types';
import { useAddAlert, useUser} from '../../store/actions';
import LoadingSpinner from 'src/shared/loading/loading';
import { getArea, getGroupTable, getGroups, getSiteByKey, getSiteKeysForSearch } from 'src/apis/getFuelSampleCalls';
import { GroupRow } from './GroupRow';
import Tooltip from '@mui/material/Tooltip';
import { Area } from './Areas/Area';
import { Site } from './Sites/Site';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { alertMessages } from 'src/constants';
import { BreadCrumb } from 'src/shared/BreadCrumbs/BreadCrumb';
import { ROUTE_PATHS } from 'src/routes';
import { modalStyle } from 'src/shared/Styles/Styles';

interface Data {
  blank_column: string,
  group_name: string,
  group_edit: string,
  group_owners: string,
  area_name: string,
  area_edit: string,
  area_owners: Contact[],
  status: string,
}

type Order = 'asc' | 'desc';
/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'blank_column',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },
  {
    id: 'group_name',
    numeric: false,
    disablePadding: true,
    label: 'GROUP NAME',
  },
  {
    id: 'group_edit',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },
  {
    id: 'group_owners',
    numeric: false,
    disablePadding: false,
    label: 'GROUP EDITOR(S)',
  },
  {
    id: 'area_name',
    numeric: false,
    disablePadding: false,
    label: 'AREA NAME',
  },
  {
    id: 'area_edit',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },

  {
    id: 'area_owners',
    numeric: false,
    disablePadding: false,
    label: 'AREA EDITOR(S)',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'GROUP STATUS',
  },
  {
    id: 'blank_column',
    numeric: false,
    disablePadding: true,
    label: 'Null',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort}=
    props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          headCell.label === 'Null' ?
            (
              <td key ={`${headCell.id}-check`} style={{padding: 'checkbox'}} className="headerParent" >&nbsp;</td>
            ) : (
              <TableCell
                className='headerParent'
                key={headCell.id}
                tabIndex={0}
                align={headCell.numeric ? 'right' : 'left'}
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                {
                (headCell.id === 'area_owners') || (headCell.id === 'group_owners') ? 
                ( 
                <span>{headCell.label}</span>
                ) 
                : 
                (
                  <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
              ) : null}

                  </TableSortLabel>  
                )
                }
                </TableCell>
            )
        ))}
      </TableRow>
    </TableHead>
  );
}

/***
 * This is the Field Sample parent Page
 * The route will have to be fixed from when a user clicks on it from
 * the pop up station route and coming from the header.
 * 
 */

const GroupStatus = [
  { label: 'All' },
  { label: 'Active' },
  { label: 'Inactive' },
];


export const FieldSampleParent: React.FC = () => {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('area_name');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openGroupModal, setOpenGroupModal] = React.useState(false);
  const [openSiteModal, setOpenSiteModal] = React.useState(false);

  const [groupStatusSelection, setGroupStatusSelection] = useState<string|undefined>(GroupStatus[0].label);
  const [inputGroupStatus, setInputGroupStatus] = useState('');


  // ** AREA CONST ***//
  const [openAddAreaModal, setOpenAddAreaModal] = React.useState(false);
  const allObjArea =  useMemo(()=>({areaName: "All",areaId: "All"}),[]);
  const [areaList, setAreaList] = useState<any[]>([allObjArea]);
  const [inputArea, setInputArea] = useState('');
  const [areaFailureCount, setAreaFailureCount] = useState(0);
  const [createUpdateAreaSuccess, setCreateUpdateAreaSuccess] = useState<boolean>(false);

    // ** GROUP CONST ***//
  const allObjGroup = useMemo(()=>({groupName: "All", groupId: 0, status: "Active", area:{areaId:'All', areaName: 'All'}}),[]);
  const [groupList, setGroupList] = useState<any[]>([allObjGroup]);
  const [inputGroupValue, setInputGroupValue] = useState('');
  const [groupFailureCount, setGroupFailureCount] = useState(0);
  const [createUpdateGroupSuccessful, setCreateUpdateGroupSuccessful] = useState<boolean>(false);

  // ** GROUP TABLE CONST **//
  const [tableFail, setTableFail] = useState(0);
  const [groupTable, setGroupTable] = useState<any[]>([]);
  const [searchSiteKeys, setSearchSiteKeys] = useState<any[]>([]);
  const [callComplete, setCallComplete] = useState<boolean>(true);
  const [editGroupBool, setEditGroupBool] = useState<boolean>(false);
  const [groupToEdit, setGroupToEdit] = useState<any>({});
	const [state,dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const setUser = useUser(dispatch);
  const [token, setToken] = useState<string|null>(null);
  const [siteCreatedSuccess, setSiteCreatedSuccess] = useState<any>(null);
  const history = useHistory();
  const parentPageLocation: any = useLocation();
  const parentPageLocationState =  parentPageLocation.state;

   // ** FOR BREADCRUMBS TRACKING **//
  const queryGroupId: number =  (!parentPageLocationState?.queries ? 0 : parentPageLocationState?.queries.groupId);
  const queryGroupName: string =  (!parentPageLocationState?.queries ? 'All' : parentPageLocationState?.queries.groupName);
  const [groupSelection, setGroupSelection] = useState<string>(!parentPageLocationState?.queries? groupList[0].groupName: queryGroupName);

  const queryAreaId: number =  (!parentPageLocationState?.queries ? 0 : parentPageLocationState?.queries.areaId);
  const queryAreaName: string =  (!parentPageLocationState?.queries ? 'All' : parentPageLocationState?.queries.areaName);
  const [areaSelection, setAreaSelection] = useState<any>(!parentPageLocationState?.queries?.areaId? areaList[0]: {areaId: queryAreaId, areaName: queryAreaName});

  const [callDoneTable, setCallDoneTable] =useState<boolean>(false)
  useEffect(()=>{
    if(!localStorage.getItem('user') && (!state.user || !checkUserFieldSampleCollector(state.user))){
      history.push('/ui')
    }
  },[state.user, history]);


  //* Gets the table data via API */
  const responseTableGroup = useCallback((request: any) => {
    setCallDoneTable(true);
    (async () => {
      try {
        let tempGroup = groupTable;
        const groupsResponse: any = await getGroupTable(request);
        setTotalCount(groupsResponse.pageInfo.total_count);
        if(tempGroup && tempGroup.length>0){
          if(groupsResponse && groupsResponse.groups && groupsResponse.groups.length>0){
            let tempResponseGroups = groupsResponse.groups;
            tempResponseGroups.forEach((tGroup:any)=>{
              let matchGroupId =tempGroup.find( (g) => g.groupId ===tGroup.groupId )
              if(matchGroupId){
                tGroup.openGroupAccordion = !matchGroupId.openGroupAccordion? false: matchGroupId.openGroupAccordion
              }
              else{
                tGroup.openGroupAccordion = false;
              }
            });
            setGroupTable(tempResponseGroups)
          }else{
            tempGroup = groupsResponse.groups;
            tempGroup.forEach((group)=>{
              group.openGroupAccordion = false;
            })
            setGroupTable(tempGroup);
          }
        }
        else{
          tempGroup = groupsResponse.groups;
          tempGroup.forEach((group)=>{
            if (parentPageLocation?.pathname.includes('group')){
              group.openGroupAccordion = true;
            }else{
            group.openGroupAccordion = false;
            }
          })
          setGroupTable(tempGroup);
        }
      }
      catch (error) {
        setTableFail(tableFail+1);
        setCallComplete(true);
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [addAlert, tableFail,groupTable, parentPageLocation?.pathname]);


  //* this gets the response from the group call to populate the group dropdown*/
  const responseGroup = useCallback((request: any, areaIdParam: any) => {
    (async () => {
      try {
        const groups: any = await getGroups(request, areaIdParam);
        const groupsUniqueName:any[] = [...new Map(groups.map(item =>
         [item['groupName'], item])).values()];
        groupsUniqueName.sort(function(a, b) {
          return a.groupName.localeCompare(b.groupName);
       })
        groupsUniqueName.unshift(allObjGroup);
        setGroupList(groupsUniqueName);
        const siteKeys:any = await getSiteKeysForSearch(token, addAlert);
        setSearchSiteKeys(siteKeys);
      }
      catch (error) {
        setGroupFailureCount(groupFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [allObjGroup, groupFailureCount,token, addAlert]);


  //**this gets the response from the area call to populate the area dropdown */
  const responseArea = useCallback((request: any, areaIdParam?: string) => {
    (async () => {
      try {
        const area: any = await getArea(request, areaIdParam);
        area.unshift(allObjArea);
        setAreaList(area);
        if (!areaIdParam) {
          const requestGroup ={
            token: token,
            addAlert:addAlert,
            setCallComplete: setCallComplete,
            setGroupFailureCount: setGroupFailureCount,
            groupFailureCount: groupFailureCount
          }
          responseGroup(requestGroup, areaSelection.areaName)
        }

      }
      catch (error) {
        setAreaFailureCount(areaFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [allObjArea, areaFailureCount, addAlert, areaSelection, groupFailureCount, responseGroup, token]);
  

  useEffect(()=>{
    /** find access token and set to send to bearer */
    if(state.userTokens && token) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
    else if(localStorage.getItem('user') && state.userTokens && !token){
      /** find access token and set to send to bearer */
      const parsedSession = JSON.parse(localStorage.getItem('user')??'');
      setUser(parsedSession.userInfo);
      setToken(state.userTokens.accessToken);
    }
    else if(!state.user){
      const parsedSession = JSON.parse(localStorage.getItem('user')??'')
      setUser(parsedSession.userInfo)    
    }
  },[state.userTokens, setUser, setToken, state.user, token]);


  //the first call to get the arealist
  useEffect(()=>{
    if( token && areaList.length<2 && areaFailureCount<2){
      const requestAreaReq = {
        setCallComplete: setCallComplete,
        addAlert: addAlert,
        token: token,
        setAreaFailureCount: setAreaFailureCount,
        areaFailureCount: areaFailureCount
      }
      responseArea(requestAreaReq);
    }
  },[token, areaFailureCount, groupFailureCount, areaList.length, addAlert, responseArea])
  
  /* below will catch when path is in group (when it is filtered from breadcrumb) and when group selection is all*/
  useEffect(()=>{
    if (!parentPageLocation?.pathname?.includes('area') && !parentPageLocation?.pathname?.includes('group') ){
      history.push(`${ROUTE_PATHS.field_samples}`)
    }
  },[groupSelection, parentPageLocation?.pathname, history])

  //The first call to get the table
  useEffect(()=>{
    if ((parentPageLocation?.pathname!== ROUTE_PATHS.field_samples) && !callDoneTable){
      setGroupSelection(queryGroupName);
      const groupUpdate ={
        pageParam: page,
        perPageParam: rowsPerPage,
        returnAll: false,  
        groupId: queryGroupId,
        areaId: areaSelection.areaName,
        status: groupStatusSelection,
        sortBy: orderBy,
        sortOrder:order,
        addAlert: addAlert,
        token:null,
        setCallComplete: setCallComplete,
        setTableFail: setTableFail,
        tableFail: tableFail
      }
      responseTableGroup(groupUpdate);
    }
    else if(groupTable && tableFail<2 && page===0 && !callDoneTable){
      const groupUpdate ={
          pageParam: page,
          perPageParam: rowsPerPage,
          returnAll: false,  
          groupName: groupSelection,
          areaId: areaSelection.areaName,
          status: groupStatusSelection,
          sortBy: orderBy,
          sortOrder:order,
          addAlert: addAlert,
          token:null,
          setCallComplete: setCallComplete,
          setTableFail: setTableFail,
          tableFail: tableFail
        }
        responseTableGroup(groupUpdate);
      
  }
  },[addAlert, groupTable, rowsPerPage, tableFail, responseTableGroup, page, parentPageLocation?.pathname,
    groupSelection, areaSelection, groupStatusSelection, order, orderBy, queryGroupId, queryGroupName, callDoneTable]);

  useEffect(() => {
    if(createUpdateGroupSuccessful) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const groupUpdate ={
        pageParam: page,
        perPageParam: rowsPerPage,
        returnAll: false,
        groupName: groupSelection,
        areaId: areaSelection.areaName,
        status: groupStatusSelection,
        sortBy: orderBy,
        sortOrder:order,
        addAlert: addAlert,
        token:token,
        setCallComplete: setCallComplete,
        setTableFail: setTableFail,
        tableFail: tableFail
      }
      responseTableGroup(groupUpdate);

      const requestGroup ={
        token: token,
        addAlert:addAlert,
        setCallComplete: setCallComplete,
        setGroupFailureCount: setGroupFailureCount,
        groupFailureCount: groupFailureCount
      }
      responseGroup(requestGroup, areaSelection.areaName);
      setCreateUpdateGroupSuccessful(false);
    }
    
  }, [addAlert, rowsPerPage, tableFail, responseTableGroup, token, page, groupSelection, areaSelection, groupStatusSelection, createUpdateGroupSuccessful, groupFailureCount, order, orderBy, responseGroup]);

  useEffect(()=>{
    if(siteCreatedSuccess){
      (async () => {
        try {
          const siteKeys:any = await getSiteKeysForSearch(token, addAlert);
          setSearchSiteKeys(siteKeys);
        }
        catch (error) {
          errorResponse(error, addAlert, alertMessages.saveSite);
        }
      })();
    }
  },[addAlert, token, siteCreatedSuccess])

//This will make a call if a new area is added
  useEffect(()=>{
    if(createUpdateAreaSuccess){
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const requestAreaReq = {
        setCallComplete: setCallComplete,
        addAlert: addAlert,
        token: token,
        setAreaFailureCount: setAreaFailureCount,
        areaFailureCount: areaFailureCount
      }
      responseArea(requestAreaReq);
      const areaUpdate ={
        pageParam: page,
        perPageParam: rowsPerPage,
        returnAll: false,
        groupName: groupSelection,
        areaId: areaSelection.areaName,
        status: groupStatusSelection,
        sortBy: orderBy,
        sortOrder:order,
        addAlert: addAlert,
        token:token,
        setCallComplete: setCallComplete,
        setTableFail: setTableFail,
        tableFail: tableFail
      }
      responseTableGroup(areaUpdate);
      setCreateUpdateAreaSuccess(false);
    }
  },[createUpdateAreaSuccess,addAlert, rowsPerPage, tableFail, responseTableGroup, order, orderBy,
    token, page, groupSelection, areaSelection, groupStatusSelection, areaFailureCount, responseArea]);

  const handleOpenAreaModel = () => {
    setOpenAddAreaModal(true);
  }

  const handleCloseAddAreaModel = (_event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      setOpenAddAreaModal(false);
    }

    setOpenAddAreaModal(false);
  }

  const handleOpenGroup = () => {
    setEditGroupBool(false);
    setGroupToEdit({});
    setOpenGroupModal(true);
  };
  
  const handleCloseGroup = (_event:any, _reason:any) => {
    setOpenGroupModal(false);
  }

  const handleOpenSite = () => setOpenSiteModal(true);

  const handleCloseSite = (event, reason) => {
    setOpenSiteModal(false);
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const updatePage ={
      pageParam: newPage,
      perPageParam: rowsPerPage,
      returnAll: false,
      groupName: groupSelection,
      areaId: areaSelection.areaName,
      status: groupStatusSelection,
      sortBy: orderBy,
      sortOrder:order,
      addAlert: addAlert,
      token:token,
      setCallComplete: setCallComplete,
      setTableFail: setTableFail,
      tableFail: tableFail
    }
    responseTableGroup(updatePage)
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPage ={
      pageParam: 0,
      perPageParam: Number.parseInt(event.target.value, 10),
      returnAll: false,
      groupName: groupSelection,
      areaId: areaSelection.areaName,
      status: groupStatusSelection,
      sortBy: orderBy,
      sortOrder:order,
      addAlert: addAlert,
      token:token,
      setCallComplete: setCallComplete,
      setTableFail: setTableFail,
      tableFail: tableFail
    }
    responseTableGroup(updatePerPage)
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
    ) => {
    const isAsc = (orderBy === property) && (order === 'asc');
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    const groupUpdate ={
      pageParam: page,
      perPageParam: rowsPerPage,
      returnAll: false,
      groupName: groupSelection,
      areaId: areaSelection.areaName,
      status: groupStatusSelection,
      sortBy: property,
      sortOrder: (isAsc ? 'desc' : 'asc'), // ???
      // sortOrder: ( orderBy === 'area_name' ? null : order ), 
      addAlert: addAlert,
      token:token,
      setCallComplete: setCallComplete,
      setTableFail: setTableFail,
      tableFail: tableFail
      }
      responseTableGroup(groupUpdate);
    };
  
  const handleSearchSites=(e:React.SyntheticEvent<Element, Event>, incomingVal: any)=>{
    const val: any = incomingVal.site_id;
    if(val){
      (async()=>{
        try{
          const siteKeys:any = await getSiteByKey(token,val);
          if(siteKeys.length===1){
            const siteKey:any = {...siteKeys[0]} 
            history.push({
              pathname:`/field_sample/${siteKeys[0].siteId}`,
              state: siteKey
            });
          }
        }
        catch(error){
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData)
        }
      })();
    }
	}

  const editGroup = (group: any) => {
    setEditGroupBool(true);
    setGroupToEdit(group);
    setOpenGroupModal(true);
  }

const buildBreadcrumbs = () => {
  if(parentPageLocation?.pathname.includes('group')){
    return(   <BreadCrumb finalCrumb={groupSelection} prevCrumb={[{ label: 'Field Sample Home', path: ROUTE_PATHS.field_samples, queries: {groupName: "All", groupId: null, areaId: 'All', areaName: 'All'} }, 
    { label: parentPageLocation.state?.queries?.areaBread, path: `/area/${parentPageLocation.state?.queries?.areaBreadId}`, queries: {groupName: "All", groupId: null, areaName: parentPageLocation.state?.queries?.areaBread, areaId: parentPageLocation.state?.queries?.areaBreadId} }]} /> )
  }
  else if(parentPageLocation?.pathname.includes('area')){
    return(   <BreadCrumb finalCrumb={parentPageLocation.state?.queries?.areaName} prevCrumb={[{ label: 'Field Sample Home', path: ROUTE_PATHS.field_samples, queries: {groupName: "All", groupId: null, areaId: 'All', areaName: 'All'} }]}/> )
  }
  else{
    return ""
  }

}

  return (
    <div className='table-page-container' data-testid={'table-page-container'} style={{overflowX: 'hidden'}}>
      <Modal //Modal to open the Area Modal
        className="smooth-scrollbar"
        open={openAddAreaModal}
        onClose={handleCloseAddAreaModel}
     >
        <div>
          <IconButton aria-label='close' style={{left: '73.5%', top: '5.5%', position: 'absolute', cursor: 'pointer'}} onClick={()=>{setOpenAddAreaModal(false)}}>
            <CloseIcon style={{color: 'white'}}/>
          </IconButton>
          <Box sx={modalStyle} className="smooth-scrollbar removePadding" >
            {
                <Area setOpen={setOpenAddAreaModal} setCreateUpdateAreaSuccess={setCreateUpdateAreaSuccess}/>
            }
          </Box>
        </div>
      </Modal>

      <Modal //Modal to open the Group Modal
        className="smooth-scrollbar"
        open={openGroupModal}
        onClose={handleCloseGroup}
      >
        <div>
          <IconButton aria-label='close' style={{left: '73.5%', top: '4.5%', position: 'absolute', cursor: 'pointer'}} onClick={()=>{setOpenGroupModal(false)}}>
            <CloseIcon style={{color: 'white'}}/>
          </IconButton>
          <Box sx={modalStyle} className="smooth-scrollbar removePadding">
            {
              editGroupBool ? (
                <Group setOpen={setOpenGroupModal} open={openGroupModal} editGroup={groupToEdit} setCreateUpdateGroupSuccessful={setCreateUpdateGroupSuccessful}/>

              ) :(
                <Group setOpen={setOpenGroupModal} open={openGroupModal} setCreateUpdateGroupSuccessful={setCreateUpdateGroupSuccessful}/>
              )
            }
          </Box>
        </div>

      </Modal>

      <Modal //Modal to open the Site Modal
        className="smooth-scrollbar"
        open={openSiteModal}
        onClose={handleCloseSite}
      >
        <div>
          <IconButton aria-label='close' style={{left: '73.5%', top: '4.5%', position: 'absolute', cursor: 'pointer'}} onClick={()=>{setOpenSiteModal(false)}}>
            <CloseIcon style={{color: 'white'}}/>
          </IconButton>
          <Box sx={modalStyle} className="smooth-scrollbar removePadding">
            {
              <Site setOpen={setOpenSiteModal} setSiteCreatedSuccess={setSiteCreatedSuccess} />
            }
          </Box>
        </div>
      </Modal>

{buildBreadcrumbs()}

        <GridContainer className="margin-top-3 dropdown-style">
          <Grid row gap="md" style={{marginBottom: '2.5%'}}>
            <Grid desktop={{ col: 8 }}><span className="font-sans-xl text-bold">Field Samples</span></Grid>
            <Grid style={{ textAlign: 'right' }}>
              <div className='searchSites'>
                <i className="fas fa-search" />

                <Autocomplete
                  freeSolo
                  onChange={(e, value) =>handleSearchSites(e,value)}
                  options={searchSiteKeys.map((option, index) => ({
                    ...option,
                    uniqueKey: `${option.site_key}-${index}`
                  }))}
                  getOptionLabel={(option) => option.display_key}
                  filterOptions={(options, state)=>{
                    const inputValue = state.inputValue.trim().toLowerCase().split(/[ -]+/);
                    const filtered = options.filter((option)=>{
                      const optionWords = option.site_key.toLowerCase().split(/[ -]+/)

                      return inputValue.every((word)=>{
                        return optionWords.some((optionWord)=> optionWord.startsWith(word))
                      })
                    })
                    return filtered
                  }}
                  renderInput={(params) => (
                    <TextField
                      onInput={(e) => validateCharacters(e, ValidCharacters.TextField, ValidCharacters.Hyphen)}
                      data-testid={'search-all-sites'}
                      {...params}
                      label="Search All Sites..."
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.uniqueKey}>
                      {option.display_key}
                    </li>
                  )}
                />
              </div>
            </Grid>
          </Grid>
        <Grid row gap="md">
          <Grid desktop={{ col: 2 }}>
            <label htmlFor='area-selection' className="font-sans-md text-bold">Area</label>
          </Grid>
          <Grid desktop={{ col: 2 }}>
            <label htmlFor='status-selection' className="font-sans-md text-bold">Group Status</label>
          </Grid>
          <Grid desktop={{ col: 2 }}>
            <label htmlFor='group-selection' className="font-sans-md text-bold">Groups</label>
          </Grid>
        </Grid>
        <Grid row gap="md" className="margin-top-1">

          {/*** AREA INPUT FIELD ***/}
          <Grid desktop={{ col: 2 }}>
            <Autocomplete
              className='height-55'
              aria-labelledby='area-selection'
              id='area-selection'
              value={areaSelection}
              onChange={(event: any, newValue: any) => {
                setAreaSelection(newValue);
                const requestGroup ={
                  token: token,
                  addAlert:addAlert,
                  setCallComplete: setCallComplete,
                  setGroupFailureCount: setGroupFailureCount,
                  groupFailureCount: groupFailureCount
                }
                responseGroup(requestGroup, newValue.areaName);
                const groupTable ={
                  pageParam: 0,
                  perPageParam: rowsPerPage,
                  returnAll: false,
                  groupName: groupSelection,
                  areaId: newValue.areaName,
                  status: groupStatusSelection,
                  sortBy: orderBy,
                  sortOrder:order,
                  addAlert: addAlert,
                  token:token,
                  setCallComplete: setCallComplete,
                  setTableFail: setTableFail,
                  tableFail: tableFail
                }
                responseTableGroup(groupTable);
                setPage(0);
              }}
              inputValue={inputArea}
              onInputChange={(event, newInputValue) => {
                setInputArea(newInputValue);
              }}
              disableClearable
              options={areaList}
              getOptionLabel={(option)=>option.areaName}
              renderInput={(params) => (
                <TextField                
                onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'area-search'}
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>

          {/*** GROUPS STATUS INPUT FIELD ***/}
          <Grid desktop={{ col: 2 }}>
            <Autocomplete
              className='height-55'
              aria-labelledby='status-selection'
              id='status-selection'
              disableClearable
              value={groupStatusSelection}
              onChange={(_event: any, newValue: any) => {
                setGroupStatusSelection(newValue);
                const statusUpdate ={
                  pageParam: 0,
                  perPageParam: rowsPerPage,
                  returnAll: false,
                  groupName: groupSelection,
                  areaId: areaSelection.areaName,
                  status: newValue,
                  sortBy: orderBy,
                  sortOrder:order,
                  addAlert: addAlert,
                  token:token,
                  setCallComplete: setCallComplete,
                  setTableFail: setTableFail,
                  tableFail: tableFail
                }
                responseTableGroup(statusUpdate);
                setPage(0);
              }}
              
              inputValue={inputGroupStatus}
              onInputChange={(event, newInputValue) => {
                setInputGroupStatus(newInputValue);
              }}
              options={GroupStatus.map((option) => option.label)}
              renderInput={(params) => (
                <TextField
                onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                  data-testid={'group-status-search'}
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>

           {/*** GROUPS INPUT FIELD ***/}
          <Grid desktop={{ col: 2 }}>
            <Autocomplete
              className='height-55'
              aria-labelledby='group-selection'
              id='group-selection'
              value={groupSelection}
              onChange={(event: any, newValue: any) => {
                setGroupSelection(newValue);
                const findGroup = groupList.find((group)=>group.groupName===(newValue));
                if(findGroup){      
                  const areaReq = {
                    setCallComplete: setCallComplete,
                    addAlert: addAlert,
                    token: token,
                    setAreaFailureCount: setAreaFailureCount,
                    areaFailureCount: areaFailureCount
                  }
                  responseArea(areaReq,findGroup.area.areaId);
                  const groupUpdate ={
                    pageParam: 0,
                    perPageParam: rowsPerPage,
                    returnAll: false,
                    groupName: findGroup.groupName,
                    areaId: areaSelection.areaName,
                    status: groupStatusSelection,
                    sortBy: orderBy,
                    sortOrder:order,
                    addAlert: addAlert,
                    token:token,
                    setCallComplete: setCallComplete,
                    setTableFail: setTableFail,
                    tableFail: tableFail
                  }
                  responseTableGroup(groupUpdate);
                  setPage(0);
                }
              }}
              inputValue={inputGroupValue}
              onInputChange={(event, newInputValue) => {
                setInputGroupValue(newInputValue);
              }}
              disableClearable
              options={groupList.map((option) => option.groupName)}
              renderInput={(params) => (
                <TextField
                onInput={(e) => validateCharacters(e, ValidCharacters.TextField, ValidCharacters.Hyphen)}
                  data-testid={'search-bar'}
                  {...params}
                  label=""
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>
          <Grid desktop={{ col: 2 }} />
          <Grid desktop={{ col: 4}} style={{ display: 'inline-flex' }}>

            <div style={{ width: '40%' }}>
              {
                state.user && checkAdminRole(state.user) &&
                <Tooltip title={"Add an Area"} placement="top-start">

                  <span>
                    <Button onClick={handleOpenAreaModel} variant="outlined">Add an Area</Button>
                  </span>
                </Tooltip>
              }
            </div>

            <div style={{ width: '40%' }}>
              {
                state.user && (fieldSampleAreaEditor(state.user)) && 
                <Tooltip title={"Add a Group"} placement="top-start">
                  <span>
                    <Button onClick={handleOpenGroup} variant="outlined">Add a Group</Button>
                  </span>
                </Tooltip>
              }
            </div>
            <div style={{ width: '35%' }}>
              {              
                state.user && 
                (fieldSampleGroupEditor(state.user)) &&
                <Tooltip title={ "Add a Site" } placement="top-start">
                <span>
                <Button disabled={false} onClick={handleOpenSite} variant="outlined">Add a Site</Button>
                </span>
                </Tooltip>
              }
            </div>
          </Grid>
        </Grid>
      </GridContainer>

      <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'>
        {!callComplete &&(
          <div style={{height: '30%'}}>
            <LoadingSpinner/>
          </div>
        )}

        <Table aria-label="Groups" size="small">
          <colgroup>
            <col width="2%" />
            <col width="15%" />
            <col width="8%" />
            <col width="15%" />
            <col width="15%" />
            <col width="8%" />
            <col width="15%" />
            <col width="15%" />

          </colgroup>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {groupTable.length>0 && groupTable.map((group) => {
              let thisParams ={
                token: token,
                addAlert: addAlert,
                group: group,
                areaId: group.area.areaId,
                areaName: group.area.areaName,
                editGroup: editGroup,
                setCreateUpdateAreaSuccess: setCreateUpdateAreaSuccess, 
                setSiteCreatedSuccess: setSiteCreatedSuccess,
                siteCreatedSuccess: siteCreatedSuccess,
                groupTable: groupTable,
                setGroupTable: setGroupTable,
              }
              return (<GroupRow allParams={thisParams} key={group.groupId} />)
            }
            )}
          </TableBody>
        </Table>

        
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />     
      </ TableContainer>
    </div>
  );
};